import * as Api from 'src/api'
import { type Option } from 'src/views/components/forms/formik/SelectInput'
import {
  type CourseAddFormValues,
  type CourseEditFormValues,
  type CourseSyllabusFormValues,
  type GenCodeEntry,
} from './types'

function parseIntBodyField(s: string): number | undefined {
  const t = s.trim()
  if (t === '') return undefined
  const n = Number.parseInt(t, 10)
  return Number.isFinite(n) ? n : undefined
}

function parseFloatBodyField(s: string): number | undefined {
  const t = s.trim()
  if (t === '') return undefined
  const n = Number.parseFloat(t)
  return Number.isFinite(n) ? n : undefined
}

function buildCodesPayload(
  genCodes: readonly GenCodeEntry[],
  codes: readonly string[]
): Array<{ code: string; semesters?: number[] }> {
  const semestersMap = new Map(genCodes.map((e) => [e.code, e.semesters]))
  return codes.map((code) => {
    const semesters = semestersMap.get(code) ?? []
    return semesters.length > 0 ? { code, semesters: [...semesters] } : { code }
  })
}

export function courseEditFormValuesToPutBody(values: CourseEditFormValues): Api.putAdministratorCourseParams['body'] {
  const status = parseIntBodyField(values.status)
  const degree = parseIntBodyField(values.degree)
  const typeId = parseIntBodyField(values.typeId)
  const credits = parseFloatBodyField(values.credit)
  const studySem = parseIntBodyField(values.studySem)
  const examCreditLimit = parseIntBodyField(values.examCreditLimit)
  const scoreThresholdMin = parseIntBodyField(values.scoreMinThreshold)
  const scoreThresholdMax = parseIntBodyField(values.scoreMaxThreshold)
  const facId = parseIntBodyField(values.facultyId)
  const primaryLecturerId = parseIntBodyField(values.lecturerId)
  const facs = values.faculties.map((id) => Number.parseInt(id, 10)).filter((n) => Number.isFinite(n))
  const type = parseIntBodyField(values.type)

  const failsForExpel = values.includeExpelCount ? (parseIntBodyField(values.failsCount) ?? null) : null

  return {
    name: values.description,
    nameEn: values.descriptionEn,
    code: values.code,
    ...(status !== undefined ? { status } : {}),
    ...(degree !== undefined ? { degree } : {}),
    ...(typeId !== undefined ? { typeId } : {}),
    ...(credits !== undefined ? { credits } : {}),
    chooseRegime: values.chooseRegime,
    userSem: [...values.userSem],
    general: values.general,
    ...(studySem !== undefined ? { studySem } : { studySem: null }),
    groupName: values.moduleName,
    ...(examCreditLimit !== undefined ? { examCreditLimit } : {}),
    ...(scoreThresholdMin !== undefined ? { scoreThresholdMin } : {}),
    ...(scoreThresholdMax !== undefined ? { scoreThresholdMax } : {}),
    failsForExpel,
    ...(facId !== undefined ? { facId } : {}),
    ...(primaryLecturerId !== undefined ? { primaryLecturerId } : {}),
    ...(facs.length > 0 ? { facs } : { facs: [] }),
    genCodes: buildCodesPayload(values.genCodes, values.requiredCodes),
    baseCodes: buildCodesPayload(values.genCodes, values.requiredElectiveCodes),
    otherCodes: buildCodesPayload(values.genCodes, values.optionalCodes),
    ...(type !== undefined ? { type } : {}),
    locale: values.locale,
    showInFreeCredits: values.showInFreeCredits === '' ? null : values.showInFreeCredits === '1',
    facsProgramIds: values.general
      ? values.facsProgramIds.map((id) => Number.parseInt(id, 10)).filter((n) => Number.isFinite(n))
      : [],
  }
}

/** Course PUT including syllabus fields (administrator course edit). */
export function courseAddFormValuesToPutAdministratorCourseBody(
  values: CourseAddFormValues,
  semId: number
): Api.putAdministratorCourseParams['body'] {
  const { course, syllabus } = splitCourseAddFormValues(values)
  return {
    ...courseEditFormValuesToPutBody(course),
    semId,
    syllabus: courseSyllabusFormValuesToAdministratorSyllabusSaveBody(syllabus),
  }
}

export function courseEditFormValuesToPostAdministratorCoursesBody(
  values: CourseEditFormValues,
  semId: number
): Api.postAdministratorCoursesParams['body'] {
  const facId = parseIntBodyField(values.facultyId)
  const degree = parseIntBodyField(values.degree)
  const status = parseIntBodyField(values.status)
  const typeId = parseIntBodyField(values.typeId)
  const credits = parseFloatBodyField(values.credit)
  if (
    facId === undefined ||
    degree === undefined ||
    status === undefined ||
    typeId === undefined ||
    credits === undefined
  ) {
    throw new Error('postAdministratorCourses: facId, degree, status, typeId, and credits are required')
  }

  const studySem = parseIntBodyField(values.studySem)
  const examCreditLimit = parseIntBodyField(values.examCreditLimit)
  const scoreThresholdMin = parseIntBodyField(values.scoreMinThreshold)
  const scoreThresholdMax = parseIntBodyField(values.scoreMaxThreshold)
  const primaryLecturerId = parseIntBodyField(values.lecturerId)
  const facs = values.faculties.map((id) => Number.parseInt(id, 10)).filter((n) => Number.isFinite(n))
  const type = parseIntBodyField(values.type)

  const failsForExpel = values.includeExpelCount ? parseIntBodyField(values.failsCount) : undefined

  return {
    name: values.description,
    facId,
    semId,
    degree,
    status,
    typeId,
    credits,
    chooseRegime: values.chooseRegime,
    ...(values.descriptionEn.trim() !== '' ? { nameEn: values.descriptionEn } : {}),
    ...(values.code.trim() !== '' ? { code: values.code } : {}),
    ...(values.userSem.length > 0 ? { userSem: [...values.userSem] } : {}),
    ...(examCreditLimit !== undefined ? { examCreditLimit } : {}),
    ...(scoreThresholdMin !== undefined ? { scoreThresholdMin } : {}),
    ...(scoreThresholdMax !== undefined ? { scoreThresholdMax } : {}),
    ...(failsForExpel !== undefined ? { failsForExpel } : {}),
    general: values.general,
    ...(studySem !== undefined ? { studySem } : {}),
    ...(values.moduleName.trim() !== '' ? { groupName: values.moduleName } : {}),
    ...(facs.length > 0 ? { facs } : {}),
    genCodes: buildCodesPayload(values.genCodes, values.requiredCodes),
    baseCodes: buildCodesPayload(values.genCodes, values.requiredElectiveCodes),
    otherCodes: buildCodesPayload(values.genCodes, values.optionalCodes),
    ...(primaryLecturerId !== undefined ? { primaryLecturerId } : {}),
    ...(type !== undefined ? { type } : {}),
    locale: values.locale,
    showInFreeCredits: values.showInFreeCredits === '' ? null : values.showInFreeCredits === '1',
    facsProgramIds: values.general
      ? values.facsProgramIds.map((id) => Number.parseInt(id, 10)).filter((n) => Number.isFinite(n))
      : [],
  }
}

export function courseToFormValues(course: Api.AdministratorCourse | null | undefined): CourseEditFormValues {
  return {
    description: course?.name ?? '',
    descriptionEn: course?.nameEn ?? '',
    facultyId: course?.faculty?.attributes?.id != null ? String(course.faculty.attributes.id) : '',
    code: course?.code ?? '',
    degree: course?.degree?.value != null ? String(course.degree.value) : '',
    status: course?.status?.value != null ? String(course.status.value) : '',
    typeId: course?.typeId != null ? String(course.typeId) : '',
    chooseRegime: course?.chooseRegime ?? false,
    includeExpelCount: course?.failsForExpel != null,
    failsCount: course?.failsForExpel != null ? String(course.failsForExpel) : '',
    userSem: course?.userSem ?? [],
    lecturerId:
      course?.primaryLecturerId != null && Number.isFinite(course.primaryLecturerId)
        ? String(course.primaryLecturerId)
        : '',
    credit: course?.credits != null ? Number.parseFloat(String(course.credits)).toFixed(2) : '',
    requiredCodes: (course?.genCodes?.attributes ?? []).map((item) => item.code),
    requiredElectiveCodes: (course?.baseCodes?.attributes ?? []).map((item) => item.code),
    optionalCodes: (course?.otherCodes?.attributes ?? []).map((item) => item.code),
    studySem: course?.studySem != null ? String(course.studySem) : '',
    moduleName: course?.group_name ?? '',
    faculties: (course?.facs ?? []).map((id) => String(id)),
    examCreditLimit: course?.exam_credit_limit != null ? String(course.exam_credit_limit) : '',
    scoreMinThreshold: course?.scoreThresholdMin != null ? String(course.scoreThresholdMin) : '',
    scoreMaxThreshold: course?.scoreThresholdMax != null ? String(course.scoreThresholdMax) : '',
    general: (course?.general ?? 0) !== 0,
    type: course?.type != null ? String(course.type) : '',
    locale: course?.locale ?? 'ka',
    showInFreeCredits: course?.showInFreeCredits != null ? String(course.showInFreeCredits) : '0',
    facsProgramIds: (course?.facsPrograms ?? []).flatMap((fp) => fp.programs.map((p) => String(p.id))),
    genCodes: [
      ...(course?.genCodes?.attributes ?? []).map((item) => ({
        code: item.code,
        semesters: item.semesters ?? [],
      })),
      ...(course?.baseCodes?.attributes ?? []).map((item) => ({
        code: item.code,
        semesters: item.semesters ?? [],
      })),
      ...(course?.otherCodes?.attributes ?? []).map((item) => ({
        code: item.code,
        semesters: item.semesters ?? [],
      })),
    ],
  }
}

export function courseSyllabusToFormValues(syllabus: Api.CourseSyllabus | null | undefined): CourseSyllabusFormValues {
  const currentSyllabusFile = syllabus?.syllabusFile
  const syllabusFiles: CourseSyllabusFormValues['syllabusFiles'] =
    currentSyllabusFile == null
      ? []
      : [
          {
            status: 'DONE',
            remoteFile: {
              id: currentSyllabusFile.id,
              name: currentSyllabusFile.name,
              title: currentSyllabusFile.title,
              originalName: currentSyllabusFile.originalName,
              mimeType: currentSyllabusFile.mimeType,
              size: currentSyllabusFile.size,
              extension: currentSyllabusFile.extension,
              downloads: currentSyllabusFile.downloads,
              downloadEndpoint: currentSyllabusFile.downloadEndpoint ?? '',
              isPublic: currentSyllabusFile.isPublic,
              createdAt: currentSyllabusFile.createdAt,
            },
          },
        ]

  return {
    duration: syllabus?.duration ?? '',
    lecturers: syllabus?.lecturers ?? '',
    hours: syllabus?.hours ?? '',
    prerequisites: syllabus?.prerequisites ?? '',
    methods: syllabus?.methods ?? '',
    mission: syllabus?.mission ?? '',
    topics: syllabus?.topics ?? '',
    outcomes: syllabus?.outcomes ?? '',
    evaluation: syllabus?.evaluation ?? '',
    resources: syllabus?.resources ?? '',
    otherResources: syllabus?.otherResources ?? '',
    schedule: syllabus?.schedule ?? '',
    knowledge: syllabus?.knowledge ?? false,
    applying: syllabus?.applying ?? false,
    judgments: syllabus?.judgments ?? false,
    communications: syllabus?.communications ?? false,
    learning: syllabus?.learning ?? false,
    values: syllabus?.values ?? false,
    syllabusFiles,
  }
}

export function splitCourseAddFormValues(values: CourseAddFormValues): {
  readonly course: CourseEditFormValues
  readonly syllabus: CourseSyllabusFormValues
} {
  return {
    course: {
      description: values.description,
      descriptionEn: values.descriptionEn,
      facultyId: values.facultyId,
      code: values.code,
      degree: values.degree,
      status: values.status,
      typeId: values.typeId,
      chooseRegime: values.chooseRegime,
      includeExpelCount: values.includeExpelCount,
      failsCount: values.failsCount,
      userSem: values.userSem,
      lecturerId: values.lecturerId,
      credit: values.credit,
      requiredCodes: values.requiredCodes,
      requiredElectiveCodes: values.requiredElectiveCodes,
      optionalCodes: values.optionalCodes,
      studySem: values.studySem,
      moduleName: values.moduleName,
      faculties: values.faculties,
      examCreditLimit: values.examCreditLimit,
      scoreMinThreshold: values.scoreMinThreshold,
      scoreMaxThreshold: values.scoreMaxThreshold,
      general: values.general,
      type: values.type,
      locale: values.locale,
      showInFreeCredits: values.showInFreeCredits,
      facsProgramIds: values.facsProgramIds,
      genCodes: values.genCodes,
    },
    syllabus: {
      duration: values.duration,
      lecturers: values.lecturers,
      hours: values.hours,
      prerequisites: values.prerequisites,
      methods: values.methods,
      mission: values.mission,
      topics: values.topics,
      outcomes: values.outcomes,
      evaluation: values.evaluation,
      resources: values.resources,
      otherResources: values.otherResources,
      schedule: values.schedule,
      knowledge: values.knowledge,
      applying: values.applying,
      judgments: values.judgments,
      communications: values.communications,
      learning: values.learning,
      values: values.values,
      syllabusFiles: values.syllabusFiles,
    },
  }
}

export function courseSyllabusFormValuesToAdministratorSyllabusSaveBody(
  values: CourseSyllabusFormValues
): Api.postAdministratorCoursesParams['body']['syllabus'] {
  const uploadedSyllabusFile = values.syllabusFiles.find((file) => file.status === 'DONE')
  return {
    duration: values.duration,
    lecturers: values.lecturers,
    hours: values.hours,
    prerequisites: values.prerequisites,
    methods: values.methods,
    mission: values.mission,
    topics: values.topics,
    outcomes: values.outcomes,
    evaluation: values.evaluation,
    resources: values.resources,
    otherResources: values.otherResources,
    schedule: values.schedule,
    knowledge: values.knowledge,
    applying: values.applying,
    judgments: values.judgments,
    communications: values.communications,
    learning: values.learning,
    values: values.values,
    ...(uploadedSyllabusFile != null ? { syllabusFileUid: String(uploadedSyllabusFile.remoteFile.id) } : {}),
  }
}

export function programsListToOptions(programs: Api.AdministratorProgramsListResponse['data']): readonly Option[] {
  const seen = new Set<string>()
  const out: Option[] = []
  for (const p of programs) {
    if (seen.has(p.code)) continue
    seen.add(p.code)
    out.push({ value: p.code, label: p.code })
  }
  return out
}

export function defaultCreateCourseStatusValue(statuses: readonly Api.Definitions[] | null | undefined): string {
  if (statuses == null || statuses.length === 0) return ''
  const active = statuses.find((s) => {
    const label = s.label.trim().toLowerCase()
    return label.includes('active') || s.label.includes('აქტიური')
  })
  return active != null ? String(active.value) : ''
}

export function courseListDefaultsToFormFields(
  defaults: Api.AdministratorCourseDefaults | null | undefined
): Partial<Pick<CourseEditFormValues, 'examCreditLimit' | 'scoreMinThreshold' | 'scoreMaxThreshold'>> {
  if (defaults == null) return {}
  return {
    examCreditLimit: String(defaults.examCreditLimit),
    scoreMinThreshold: String(defaults.scoreThresholdMin),
    scoreMaxThreshold: String(defaults.scoreThresholdMax),
  }
}
